import React from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { PlayerControl, PlayerIcon, PlayingAsset, PlayingMode, PropTypesAdditional, PropTypesPlayerUICommon } from 'types/Player';
import { ChannelLogo, ChannelLogoFallback } from 'components/PlayerUI/PlayerUI.css';
import { Picture } from 'components/Picture/Picture';
import { useRecording } from 'providers/useRecording/RecordingContext';
import {
    castEnabled,
    getBroadcastPlayerActionComponents,
    getBroadcastPlayerMetaData,
    getBroadcastPlayerMoreOverlayMetaData,
    isSeriesPlayerAsset,
} from 'utils/fnPlayerUI';
import { getLivePlayerAssetContentMarker } from 'utils/fnContentMarker';
import { isLivePlayerAsset } from 'utils/fnTypeGuards';
import { useBroadcastTimeCluster } from 'hooks/useBroadcastTimeCluster/useBroadcastTimeCluster';
import { getRecordingIconName } from 'utils/fnData';
import { TimeClusterRecordingTypeMap } from 'types/RecordingTypes';

export const useLivePlayerUI = (common: PropTypesPlayerUICommon, asset: PlayingAsset): PropTypesAdditional => {
    const timeCluster = useBroadcastTimeCluster(isLivePlayerAsset(asset) ? asset : {});
    const { isRecorded } = useRecording();

    if (!isLivePlayerAsset(asset)) {
        return null;
    }

    const { channelLogo, channelName, title, subtitle, startTime, endTime, logicalChannelNumber, description, cast, originalLanguages } =
        asset;

    const nowSeconds = Math.floor(Date.now() / 1000);
    const isReplayAllowed = asset.replayUntil != null && asset.replayUntil > nowSeconds;

    const timeShiftEnableDuration = endTime - startTime < 21600000;

    const getIcons = (): PlayerIcon[] => {
        const metaIcons: PlayerIcon[] = [];

        if (isRecorded) {
            metaIcons.push(getRecordingIconName(TimeClusterRecordingTypeMap.LIVE) as PlayerIcon);
        }

        return metaIcons;
    };

    const getControls = (): PlayerControl[] => {
        const controls: PlayerControl[] = [PlayerControl.FULL_SCREEN, PlayerControl.VOLUME];

        if (castEnabled()) {
            controls.push(PlayerControl.CAST);
        }

        controls.push(PlayerControl.INFO);

        controls.push(PlayerControl.SUBTITLE);

        if (isReplayAllowed && !isMobile && !isTablet) {
            controls.push(PlayerControl.JUMP_BACK);
            controls.push(PlayerControl.JUMP_FORWARD);
        }

        controls.push(PlayerControl.PLAY_PAUSE);

        if (isReplayAllowed) {
            controls.push(PlayerControl.GO_TO_LIVE);
        }

        if (isSeriesPlayerAsset(asset)) {
            controls.push(PlayerControl.EPISODES);
            controls.push(PlayerControl.NEXT_ASSET);
        }

        return controls;
    };

    const getDisabledControls = (): PlayerControl[] => {
        const controls: PlayerControl[] = [];

        if (common.playingMode === PlayingMode.NORMAL) {
            controls.push(PlayerControl.JUMP_FORWARD, PlayerControl.GO_TO_LIVE);
        }

        if (!isReplayAllowed) {
            controls.push(PlayerControl.JUMP_BACK, PlayerControl.JUMP_FORWARD, PlayerControl.GO_TO_LIVE);
        }

        return controls;
    };

    const controls = getControls();
    const disabledControls = getDisabledControls();

    const { actionPopup, mobileOverlayActionHandlers } = getBroadcastPlayerActionComponents(controls);

    return {
        logo: (channelLogo || channelName) && (
            <ChannelLogo>
                <Picture src={channelLogo} hPosition={'left'} vPosition={'center'} fit={'scale-down'}>
                    {channelName && <ChannelLogoFallback>{channelName}</ChannelLogoFallback>}
                </Picture>
            </ChannelLogo>
        ),
        logicalChannelNumber,
        metaData: getBroadcastPlayerMetaData(asset),
        moreOverlayMetaData: getBroadcastPlayerMoreOverlayMetaData(asset),
        contentMarker: getLivePlayerAssetContentMarker(asset, timeCluster),
        metaIcons: getIcons(),
        controls,
        disabledControls,
        seekEnabled: isReplayAllowed && timeShiftEnableDuration,
        title,
        subtitle,
        actionPopup,
        mobileOverlayActionHandlers,
        description,
        cast,
        originalLanguages,
    };
};
